import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from './Footer';
import { company } from '../mock';
import { useLang } from '../i18n';
import { useViewContext } from '../viewContext';
import { ThemeToggle } from './ThemeToggle';

export const LegalSection = ({ number, title, children }) => (
  <section className="border-t border-white/10 py-10 first:border-t-0 first:pt-0">
    <div className="grid md:grid-cols-[80px_minmax(0,1fr)] gap-4 md:gap-10">
      {number && <div className="label-mono text-ember pt-1">{number}</div>}
      <div className={number ? '' : 'md:col-span-2'}>
        {title && <h2 className="font-display font-medium text-white text-2xl md:text-[28px] leading-snug mb-5">{title}</h2>}
        <div className="space-y-4 text-white/70 font-body text-[15px] md:text-base leading-relaxed">{children}</div>
      </div>
    </div>
  </section>
);

export const LegalList = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((it) => (
      <li key={it} className="flex items-start gap-3">
        <span className="mt-2 w-1.5 h-1.5 bg-ember/80 shrink-0" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export const LegalBlock = ({ label, lines }) => (
  <div>
    {label && <div className="label-mono text-white/40 mb-1.5">{label}</div>}
    {lines.map((l, i) => (
      <div key={i} className="text-white/85">{l}</div>
    ))}
  </div>
);

// Resolves a data-driven block definition into concrete lines using company facts.
const resolveBlock = (b) => {
  if (b.type === 'company') return { label: b.label, lines: [company.name, ...company.address] };
  if (b.type === 'emails') return { label: b.label, lines: company.emails };
  if (b.type === 'responsible') return { lines: ['Dominik M. Schäfer', company.name, ...company.address] };
  return { label: b.label, lines: b.lines || [] };
};

export const LegalDocument = ({ doc, updated }) => (
  <LegalLayout title={doc.title} updated={updated}>
    {doc.sections.map((s, i) => (
      <LegalSection key={i} number={s.number} title={s.title}>
        {s.text && s.text.map((p, j) => <p key={`t${j}`}>{p}</p>)}
        {s.list && <LegalList items={s.list} />}
        {s.textAfter && s.textAfter.map((p, j) => <p key={`ta${j}`}>{p}</p>)}
        {s.blocks && s.blocks.map((b, j) => {
          const r = resolveBlock(b);
          return <LegalBlock key={`b${j}`} label={r.label} lines={r.lines} />;
        })}
      </LegalSection>
    ))}
  </LegalLayout>
);

const LegalLayout = ({ title, updated, children }) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const { setView } = useViewContext();
  useEffect(() => {
    setView(`The visitor is reading the "${title}" legal page.`);
    return () => setView('');
  }, [title, setView]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') navigate('/'); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  return (
    <div className="bg-navy min-h-screen flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-navy/90 backdrop-blur-xl border-b border-navy/10 dark:border-white/10">
        <div className="max-w-[1680px] mx-auto px-6 md:px-10 h-[84px] flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="/brand/intervantis-horizontal-dark.png" alt="Intervantis" className="h-[46px] md:h-[52px] w-auto object-contain dark:brightness-0 dark:invert" draggable="false" />
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Link to="/" className="group inline-flex items-center gap-2.5 rounded-full bg-ember hover:bg-ember-hover text-white px-5 py-3 label-mono shadow-lg shadow-ember/20 transition-colors">
              <ArrowLeft size={17} strokeWidth={2.2} className="transition-transform group-hover:-translate-x-1" />
              {t('ui.backToSite')}
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="section-teal grid-overlay flex-1">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-28 relative">
          <div className="label-mono text-ember mb-6">{t('ui.legal')} · {company.name}</div>
          <h1 className="font-display font-light text-white text-4xl md:text-5xl lg:text-[58px] leading-[1.05] tracking-tight">{title}</h1>
          {updated && <p className="mt-5 label-mono text-white/45">{t('ui.lastUpdated')} · {updated}</p>}
          <div className="mt-14">{children}</div>
        </div>
      </main>

      <Footer onTabChange={() => {}} />
    </div>
  );
};

export default LegalLayout;
